import {
    ApiAdjusment,
    ApiOrderTracking,
    ApiOrderTrackingDelivery,
    ApiPromotion,
} from '@api/_api/api-order/api-order.models';
import { CartItem } from '@api/cart/cart.models';
import { Item } from '@api/catalog/items/medias/items.models';

export interface Orders {
    orders: Order[];
    howMany: number;
    index: number;
    sfiOrders: boolean;
    totalResults: number;
}

export interface Order {
    totalFuturePrice: number;
    adjustment: ApiAdjusment[];
    assistedPurchase: number;
    billingAddressId: any;
    billingAddressIdString?: string;
    courier?: any;
    currency?: string;
    date?: any;
    daysLeftToCleanCart?: number;
    deliveryDate?: any;
    egui?: string;
    eInvoiceStatus?: any;
    giftPacking: boolean;
    giftTicket?: any;
    guestToken?: any;
    hasSubOrders: boolean;
    hasBeenRequested?: boolean;
    id: number | string;
    isCOD?: boolean;
    isEguiPrintAllowed?: boolean;
    isEnabledReturnableDateLimit: number;
    isCartGiftPacking?: boolean;
    isHazardousGood?: boolean;
    isMailRegistered?: any;
    isNoNexus?: boolean;
    isPaperless?: any;
    isPOD?: boolean;
    isReplacement: boolean;
    isRequestBankTransfer?: boolean;
    isReturnableDate: number;
    isTailoring: boolean;
    items: Item[];
    lastUpdate?: any;
    memberId?: any;
    message?: any;
    orderPricesMemberType?: OrderPriceMemberType[];
    orderTracking: any;
    payment: any[]; // revisar el any
    paymentIntentUrl: string;
    personalizationId: string;
    phonePurchase: boolean;
    phonePurchaseAgentName?: any;
    promotion: ApiPromotion[];
    repay?: any;
    repayable: boolean;
    requestedDate?: any;
    returnTypesAllowed?: string;
    rueiData?: any;
    sfiCreditEnabled: boolean;
    shipping?: any;
    shippingMethods?: any;
    shippingPrice: number;
    shippingPriceEuro?: number;
    shippingTax: number;
    shippingTaxArray: any[]; // revisar el any
    status: string;
    statusText: string;
    statusTracking?: any;
    suborders: Order[];
    subordersNumber?: any;
    tax: number;
    taxArray: any[];
    teenUserId?: any;
    totalAdjustment: number;
    totalOrder: number;
    totalOrderEuro?: number;
    totalProduct: number;
    totalTax?: any;
    totalTaxShipping?: any;
    totalPoints?: number;
    trackingNumber?: any;
    urlTracking: string;
    urlTrackingCIS?: any;
    receiptUID?: string;
    stlocId?: number;
    operationNumber?: number;
    idCaja?: number;
    isRefundDataNeeded?: boolean;
    emptyResponse?: boolean;
}

export interface OrderPriceMemberType {
    memberType: string;
    shippingPrice: number;
    shippingPriceEuro: number;
    totalAdjustment: number;
    totalOrder: number;
    totalOrderEuro: number;
    totalProduct: number;
    adjustments: ApiAdjusment[];
}

export interface Suborder {
    storeLocationId: string;
    items: CartItem[];
    deliveryDate?: string;
    preSaleType?: boolean;
    preSaleTypeKey?: string;
}

export interface Adjustment {
    some: any;
}

export interface Promotion {
    value: any;
}

export interface OrderTracking extends Omit<ApiOrderTracking, 'orderDelivery'> {
    orderDelivery: OrderTrackingDelivery[];
    emptyResponse?: boolean;
}

export interface ZalandoReturn {
    orderId: number;
    rmaId: number;
    returnCharges: {
        amount: number;
        exponent: number;
        reason: string;
        currency: string;
    };
    maxFreeReturndate: string;
}

export interface OrderTrackingDelivery extends ApiOrderTrackingDelivery {
    icon: string;
}

export enum OrderStatusStep {
    CHECK,
    NOT_FOUND,
    FOUND,
}

export interface Bank {
    activated: number;
    bankId: number;
    bankName: string;
    bankType: number;
    defaultDesc: string;
    defaultIconImg: string;
    groupId: number;
    groupType: string;
    // eslint-disable-next-line no-secrets/no-secrets
    /** @example "swiftCode=MELIAZ22" */
    parameters: string;
    position: number;
}

export interface Invoices {
    invoices: Invoice[];
    howMany: number;
    index: number;
    totalResults: number;
    personalizationId: string;
    rueiData: {
        OperationRUEI: string;
        OperationTypeRUEI: string;
        StoreIdRUEI: string;
        StoreLangRUEI: string;
        StoreTypeRUEI: string;
    };
}

export interface Invoice {
    currency: string;
    date: string;
    invoiceId: number;
    invoiceNamePDF: string;
    invoiceNumber: string;
    ordersId: number;
    rmaId: number;
    status: string;
    totalInvoice: number;
}

export interface Receipts {
    FromDate: string;
    Limit: number;
    ListReceiptSummary: Receipt[];
    NumberOfElements: number;
    Offset: number;
    ToDate: string;
    User: {
        Email: string;
        Tenant: string;
        Currency: string;
    };
}

export interface Receipt {
    CountryStoreId: number;
    Currency: string;
    Date: string;
    EcommerceId: string;
    ImageURL: string;
    ListItem: ItemReceipt[];
    NotificationChannel: string;
    NumberOfArticles: number;
    QRCode: string;
    ReceiptUID: string;
    Source: string;
    Store: {
        Name: string;
        Address: {
            Address: string;
            ZipCode: string;
            City: string;
        };
    };
    Name: string;
    StoreId: number;
    Timestamp: string;
    Total: number;
    Type: number;
    totalPoints: number;
}

export interface ItemReceipt {
    PartNumber: string;
    Quantity: number;
    IsReturned: boolean;
    ParentUuid: string[];
    ChildUuid: string[];
}

export interface ListTicketless {
    ListDocuments: ListTicketlessDocument[];
}

export interface ListTicketlessDocument {
    name: string;
    localizedName: string;
    uri: string;
}

export interface OrderRefundData {
    contactDetail: {
        email: string;
    };
    bankAccount: BankAccount;
}

export interface BankAccount {
    firstName?: string;
    // middleName?: string;
    lastName?: string;
    bankName?: string;
    bankCity?: string;
    bankInn?: string;
    bicCode?: string;
    // bban?: string;
    bankAddress?: string;
    // beneficiaryName?: string;
    bankCode?: string;
    // branchCode?: string;
    branchName?: string;
    accountType?: string;
    accountCode?: string;
    nif?: string;
    pan?: string;
    documentTypeCode?: string;
    accountholderAddress?: {
        city?: string;
        country?: string;
        address?: string;
        zipcode?: string;
    };
}

export interface OrderPartialGiftTicketData {
    orderItems: OrderPartialGiftTicketItem[];
}

export interface OrderPartialGiftTicketItem {
    orderItemId: number;
    quantity: number;
}
